import {
    convertZonetoUTC,
    getCurrentTimestamp,
    getMetricTimeStamp,
    getRelativeMonthDate,
} from "../../../Helper/DatePicker/DateConverter";
import { isSerialReport } from "../../../Api/WorkOrderApi";
import { workOrderFeatureCode } from "../../../Static/FeatureMapping";
import { selectedValues } from "../filterHelpers";
import { statusTheme } from "../statusPalette";

// Pure helpers behind the Work Orders queue, the New Work Order form, the
// serial report and the review dialog.
//
// Kept out of the components and exported so they can be unit tested directly,
// per the repo's "test the pure function, don't mount the page" rule -- the
// Network tab does the same with tableHelpers.js and validation.js. Everything
// here is a transform or a predicate: no state, no requests, no JSX.

// --------------------------------------------------------------------------
// Status vocabulary
// --------------------------------------------------------------------------

// workOrder.status. Three values and no others -- the list endpoint validates
// against exactly this set (WORK_ORDER_STATUS_VALUES in
// app/requests/work_order_requests.py).
export const WORK_ORDER_STATUS = {
    PENDING: "pending",
    APPROVED: "approved",
    REJECTED: "rejected",
};

// The HISTORY tab's default selection: everything that has been decided.
//
// The list endpoint's `status` is a CommaSeparatedStrList, not a single value
// -- `?status=approved,rejected` is one query returning both, correctly paged
// and sorted by the server. History defaulting to `approved` alone meant a
// rejected work order existed, was counted in the tab badge, and appeared
// nowhere on the tab until the user opened a dropdown to look for it.
//
// A comma-joined STRING rather than an array on purpose: the status is passed
// down as a prop and compared by identity in useWorkOrders, where a fresh
// array literal every render would clear the rows on every render.
export const HISTORY_STATUS_ALL = `${WORK_ORDER_STATUS.APPROVED},${WORK_ORDER_STATUS.REJECTED}`;

// What to call the current History selection in a downloaded filename. The
// selection is a request value, and "approved,rejected.csv" is not a filename.
export const historyStatusSlug = (status) =>
    status === HISTORY_STATUS_ALL ? "decided" : status;

// workOrder.reviewAction. `autoApprove` is NOT `approve`: it says the type had
// requiresApproval = 0, so the request was written straight through and NOBODY
// checked it. The backend keeps the two apart deliberately (see the note above
// the create log line in app/services/work_order.py) because that flag is
// hand-seeded SQL with no code-side gate -- one wrong row turns a controlled
// type self-service, and this column is the only place it shows.
export const REVIEW_ACTION = {
    APPROVE: "approve",
    REJECT: "reject",
    AUTO: "autoApprove",
};

export const isAutoApproved = (row) =>
    row?.status === WORK_ORDER_STATUS.APPROVED &&
    row?.reviewAction === REVIEW_ACTION.AUTO;

// The text in the Status cell. It is READ, never derived.
//
// `statusLabel` is computed by the server (_statusLabel in
// app/services/work_order.py) and returned by the list, the detail AND the
// export serialisers. It has to be, because the emailed attachment is built
// server-side from those export rows and never passes through any client
// formatter: a label derived here meant the screen and the download said
// "Auto-approved" while the CSV in the recipient's inbox said "approved" -- and
// that is the one row whose entire meaning is that nobody reviewed it.
//
// So there is exactly one place the four labels exist, and it is not this file.
// Do not reintroduce a client-side mapping from `status`.
export const workOrderStatusText = (row) => row?.statusLabel || "-";

// Colours for the chip. Auto-approved is amber rather than green: it is not a
// failure, but it is not a signed-off approval either, and it should not sit in
// a list looking identical to one.
//
// The ROLE is chosen here; the hexes come from the module's one ramp. Chips
// speak {color, background, border} and the ramp speaks {fg, bg, border}, so
// chipTheme is the one place that translates -- rather than every caller
// learning both vocabularies.
const chipTheme = (role) => {
    const { fg, bg, border } = statusTheme(role);
    return { color: fg, background: bg, border };
};

export const workOrderStatusTheme = (row) => {
    const status = row?.status;
    if (status === WORK_ORDER_STATUS.PENDING) return chipTheme("warning");
    if (status === WORK_ORDER_STATUS.REJECTED) return chipTheme("error");
    if (status === WORK_ORDER_STATUS.APPROVED) {
        return chipTheme(isAutoApproved(row) ? "warningSoft" : "success");
    }
    return chipTheme("neutral");
};

// The one-line explanation shown under an auto-approved row's detail header.
export const autoApprovalNote = (row) =>
    isAutoApproved(row)
        ? "Approved automatically on submission. This work order type does not require a checker, so nobody reviewed it."
        : "";

// --------------------------------------------------------------------------
// The approve / reject button predicate
// --------------------------------------------------------------------------

// Why the review buttons are not offered, or null when they are.
//
// BOTH halves are real backend rules, enforced in reviewWorkOrder():
//   - Casbin `approve` on WORK_ORDER.<TYPE>  -> 403
//   - the reviewer is not the maker                           -> 403
// Offering a button that will 403 is worse than not offering one, and hiding it
// with no explanation is how "why can't I approve this?" becomes a support
// ticket -- so the self-review case returns a REASON, which the UI shows in
// place of the button.
//
// `hasPermission` is passed in rather than read from context so this stays a
// pure function the tests can drive with a fake. Remember the v1 fallback:
// PermissionService.hasPermission returns true for RBAC v1 orgs, so a v1 org
// gets the buttons and the backend remains the only enforcement -- which is
// what every other feature in this codebase does.
export const canReviewWorkOrder = ({ row, currentUserID, hasPermission }) => {
    if (!row) return { allowed: false, reason: null };

    if (row.status !== WORK_ORDER_STATUS.PENDING) {
        return { allowed: false, reason: null };
    }

    const check = typeof hasPermission === "function" ? hasPermission : () => false;
    if (!check(workOrderFeatureCode(row.workOrderTypeCode), "approve")) {
        return { allowed: false, reason: null };
    }

    // AN UNKNOWN IDENTITY CLOSES THE GATE. This is an early return, not a
    // condition on the comparison below: guarding the self-review check with
    // "only compare when both ids are known" SKIPS it when one is not, and
    // falls through to allowed -- which showed the maker a Review button on
    // their own request during the /api/rbac/me/permissions round trip, and for
    // the whole session if that fetch failed. The backend still refuses, so
    // what the user actually got was an uninterpretable 403 toast.
    //
    // `undefined` is "still loading" and `null` is "the lookup failed"; both
    // mean the same thing here -- we cannot prove this is not the maker, so we
    // must not offer to review. canEditWorkOrder takes the same position, and
    // the two halves of one identity check have to agree.
    if (currentUserID === null || currentUserID === undefined) {
        return {
            allowed: false,
            reason: "Checking who you are… the review actions will appear once that is known.",
        };
    }

    // A row with no maker cannot be proved not to be yours either.
    if (row.createdByUserID === null || row.createdByUserID === undefined) {
        return { allowed: false, reason: null };
    }

    // Compared as strings: the row carries a number from the API and the id
    // resolved from /api/rbac/me/permissions has been through JSON too, so the
    // two spellings of the same person must not read as two people.
    if (String(row.createdByUserID) === String(currentUserID)) {
        return {
            allowed: false,
            reason: "You raised this request, so someone else has to review it.",
        };
    }

    return { allowed: true, reason: null };
};

// The maker's own controls, and WHY they are not offered.
//
// All FOUR conditions the backend applies in _loadEditableWorkOrder, in one
// place: the request still exists, it is still pending, the caller is the
// maker, and the caller holds `create` on the type. Edit and withdraw share
// this gate on the server, so they share it here.
//
// It returns a reason as well as a verdict because there are now two callers
// with different needs. The queue only needs the boolean -- it hides a row
// action. The create/edit PAGE needs the sentence: it is reachable by URL, so
// somebody can land on /work-orders/<id>/edit for an approved request or for
// another maker's, and "you do not have permission" would be the wrong thing to
// say in either case.
//
// FAILS CLOSED ON AN UNKNOWN IDENTITY, and does so as an early return rather
// than as a condition on the comparison below. `undefined` is "still loading"
// and `null` is "the lookup failed"; neither can prove this request is yours,
// and a gate that falls through to allowed while the id resolves is a bug this
// codebase has already shipped once -- see the note in canReviewWorkOrder.
export const workOrderEditGate = ({ row, currentUserID, hasPermission }) => {
    if (!row) return { allowed: false, reason: "" };

    if (row.status !== WORK_ORDER_STATUS.PENDING) {
        return {
            allowed: false,
            reason: "This work order has already been reviewed, so it can no longer be edited.",
        };
    }

    if (currentUserID === null || currentUserID === undefined) {
        return {
            allowed: false,
            reason: "We could not confirm who you are, so this request cannot be opened for editing.",
        };
    }

    // A row with no maker cannot be proved to be yours either.
    if (row.createdByUserID === null || row.createdByUserID === undefined) {
        return {
            allowed: false,
            reason: "Only the person who raised this request can edit it.",
        };
    }

    // Compared as strings: the row carries a number from the API and the id
    // resolved from /api/rbac/me/permissions has been through JSON too, so two
    // spellings of the same person must not read as two people.
    if (String(row.createdByUserID) !== String(currentUserID)) {
        return {
            allowed: false,
            reason: "Only the person who raised this request can edit it.",
        };
    }

    const check = typeof hasPermission === "function" ? hasPermission : () => false;
    if (!check(workOrderFeatureCode(row.workOrderTypeCode), "create")) {
        return {
            allowed: false,
            reason: "Your role does not allow raising this type, so you cannot edit this request.",
        };
    }

    return { allowed: true, reason: "" };
};

// The boolean half, for the queue's row actions. A wrapper and NOT a second
// implementation: two copies of a four-part gate drift, and the half that
// drifts is the half nobody is looking at.
export const canEditWorkOrder = (args) => workOrderEditGate(args).allowed;

// --------------------------------------------------------------------------
// Who may open the create / edit form at all
// --------------------------------------------------------------------------

// May this user raise a work order of ANY type?
//
// The gate on the "+ New Work Order" control, wherever it is drawn. It lives
// here rather than in a component because there are now TWO of them: the module
// shell's floating button, which is on screen from every tab, and whatever the
// Work Orders tab itself offers. Two copies of a permission predicate drift,
// and the copy that drifts is the one nobody is looking at.
//
// `create` on at least one type is the right test: the form's first field is
// the type, so a user holding the grant on any single one has somewhere to go,
// and workOrderFormAccess narrows the picker to exactly those. The create call
// is guarded per type on the server regardless.
//
// DENIES on an empty list, which includes the state before the config has
// loaded and the state where it failed. A button that appears and then
// disappears is worse than one that appears a moment late, and a button that
// leads to a form the server will refuse is worse than both.
export const canRaiseAnyWorkOrder = (workOrderTypes, hasPermission) => {
    const check = typeof hasPermission === "function" ? hasPermission : () => false;
    return (Array.isArray(workOrderTypes) ? workOrderTypes : []).some(
        (type) => type?.code && check(workOrderFeatureCode(type.code), "create")
    );
};

// The types this user may RAISE, and whether the form should open.
//
// This gate did not exist while the form was a dialog: the only way in was a
// "+ New Work Order" button the queue already hid from anyone without `create`
// on some type. The form has its own URL now, so the button is no longer the
// gate and this is -- otherwise a view-only user can type /work-orders/new into
// the address bar and fill in a form whose every submit is a 403 the server
// explains in a toast.
//
// The returned `types` is what the picker renders. Narrowing it is the other
// half of the same rule: offering a type the write will refuse is how a maker
// spends five minutes on a form that was never going to be accepted.
//
// EDIT is handed to workOrderEditGate, which applies all four of the rules
// _loadEditableWorkOrder enforces: pending, yours, and `create` on the type.
//
// hasPermission is passed in rather than read from context so this stays pure.
// Remember the v1 fallback: it returns true for RBAC v1 orgs, so a v1 org sees
// every configured type and the backend remains the only enforcement.
export const workOrderFormAccess = ({
    workOrderTypes = [],
    workOrder = null,
    // Undefined while /api/rbac/me/permissions is in flight, null when it
    // failed. Both close the edit gate -- see workOrderEditGate.
    currentUserID = undefined,
    hasPermission,
}) => {
    const check = typeof hasPermission === "function" ? hasPermission : () => false;
    const types = (Array.isArray(workOrderTypes) ? workOrderTypes : []).filter(
        (type) => type?.code && check(workOrderFeatureCode(type.code), "create")
    );

    // EDIT delegates to workOrderEditGate, which is the same four-part rule the
    // backend applies in _loadEditableWorkOrder. Checking only "may this user
    // raise this type" here was weaker than the gate the queue's hidden Edit
    // button used to provide: it opened a fully seeded, editable form for an
    // already-approved request, and for another maker's request to any checker
    // holding `create` on that type. Both of those save into a 409 or a 403.
    //
    // `types` still travels back, because the picker renders it -- the type is
    // immutable on edit, so the list only has to contain the one already
    // chosen for the disabled select to show its label.
    if (workOrder?.id) {
        const gate = workOrderEditGate({ row: workOrder, currentUserID, hasPermission });
        return { allowed: gate.allowed, reason: gate.reason, types };
    }

    return {
        allowed: types.length > 0,
        reason: types.length
            ? ""
            : "You do not have permission to raise a work order of any type.",
        types,
    };
};

// --------------------------------------------------------------------------
// Paste-to-split serial entry
// --------------------------------------------------------------------------

// What a pasted list is split on.
//
// Newline, comma, semicolon and tab -- the four things a spreadsheet column, a
// chat message or a CSV cell produce. NOT the space character: a serial may
// legitimately contain one, and splitting on it would turn one bad serial into
// two worse ones with no way for the user to tell what happened.
const SERIAL_SEPARATORS = /[\n\r,;\t]+/;

// The key both sides of a serial comparison must agree on.
//
// Mirrors serialKey() in app/database/asset/work_order_db.py, which case-folds
// because assetMeta.serialNumber is a *_ai_ci collation: "pk-2024-a19" and
// "PK-2024-A19" are the same asset to the database, so they must be the same
// chip here. JS has no casefold(); toLowerCase() differs only on scripts no
// serial format in this product uses.
export const serialKey = (serial) => String(serial ?? "").trim().toLowerCase();

// Pasted or typed text -> the chips to ADD.
//
// Returns only what is new: already-present serials are dropped rather than
// duplicated, and so are repeats within the paste itself. The user's own casing
// is preserved in the chip -- the report that comes back shows the serial they
// typed, so the two have to match by eye.
export const parseSerialInput = (text, existing = []) => {
    if (text === null || text === undefined) return [];
    const seen = new Set(
        (Array.isArray(existing) ? existing : []).map(serialKey).filter(Boolean)
    );
    const added = [];
    String(text)
        .split(SERIAL_SEPARATORS)
        .forEach((piece) => {
            const serial = piece.trim();
            if (!serial) return;
            const key = serialKey(serial);
            if (seen.has(key)) return;
            seen.add(key);
            added.push(serial);
        });
    return added;
};

// True when the text carries a separator, i.e. when it is a LIST rather than one
// serial. The paste handler uses this to decide whether to take the event over:
// a single-value paste is left to the input so the user can still edit it.
export const isMultiSerialPaste = (text) =>
    Boolean(text) && SERIAL_SEPARATORS.test(String(text));

// --------------------------------------------------------------------------
// The serials BOX
// --------------------------------------------------------------------------
//
// The form holds what the user typed, as TEXT, and the serial list is derived
// from it. It used to hold chips, which cannot survive the next stage: a
// Vendor Intake or a Delivery row is a serial PLUS columns, tab separated, so
// the thing pasted has to stay a block of text long enough to be parsed as
// one. A chip destroys the line it came from.

// The text in the box -> the serials on the movement, deduplicated, in the
// order and the casing they were typed in.
export const serialsFromText = (text) => parseSerialInput(text, []);

// The reverse, for seeding an edit and for rewriting the box after a removal.
// ONE PER LINE: the box accepts commas, semicolons and tabs as well, but
// anything this function writes is normalised, because a list the form
// rewrote should read the same way every time.
export const serialsToText = (serials = []) =>
    (Array.isArray(serials) ? serials : [])
        .map((serial) => String(serial ?? "").trim())
        .filter(Boolean)
        .join("\n");

// One movement of form state -> the shape the payload builder and the
// validator already take. They are unchanged and untouched: this is the only
// place that knows the box is text.
//
// The movement TYPE decides how many columns that text has -- see
// movementPasteSpec -- so an intake's item name and SKU are turned into
// itemOverrides here and ride to the API through the per-item path
// buildMovementPayload already had. Omitting the type gives the single-column
// reading, which is what every movement of existing assets wants.
export const resolveMovementSerials = (movement, movementType = null) => {
    const parsed = parsePastedRows(
        movement?.serialText,
        movementPasteSpec(movementType)
    );
    return {
        ...(movement || {}),
        serials: parsed.rows.map((row) => row.serialNumber),
        // Merged OVER anything the movement already carried, so a future
        // per-item editor is not silently overwritten by the paste.
        itemOverrides: {
            ...(movement?.itemOverrides || {}),
            ...movementItemOverrides(parsed),
        },
    };
};

export const resolveFormSerials = (form, movementTypesByCode = {}) => ({
    ...(form || {}),
    movements: (form?.movements || []).map((movement) =>
        resolveMovementSerials(
            movement,
            movementTypesByCode?.[movement?.movementTypeCode] || null
        )
    ),
});

// What the serial report's "Remove these serials" button does to the box.
//
// Rebuilt from the parsed list rather than by deleting LINES: one line can
// carry several comma separated serials, and dropping the whole line would
// take the good ones with the bad. The cost is that the box comes back
// normalised to one per line, which is the same thing the button promises.
// Drop the named serials from the box.
//
// A COMMA MEANS TWO DIFFERENT THINGS, and that is the whole of this function.
//
// In a SINGLE-column box a comma separates serials, so "A-1,A-2,A-3" is three
// assets and removing A-2 has to keep the other two.
//
// In a MULTI-column box a comma separates CELLS, so
// "BAT-S005, ITEM-1002, SKU-1002" is one row and removing BAT-S005 has to take
// the whole line. Treating it as a list of serials was the bug: every cell
// became a "serial", and removing three of them left the surviving CELLS,
// deduplicated and one per line --
//
//     ITEM-1002
//     SKU-1002
//     BAT-S009
//
// -- four valid rows turned into three unreadable ones. The note above
// serialsFromText warns about exactly this: an intake row is a serial PLUS
// columns, and anything that flattens the box destroys the line it came from.
//
// So the caller passes the movement's column count, because only the movement
// type knows which reading applies. Default 1, which is the single-column
// behaviour the other callers already relied on.
export const removeSerialsFromText = (text, serialsToRemove = [], { columnCount = 1 } = {}) => {
    const drop = new Set(
        (Array.isArray(serialsToRemove) ? serialsToRemove : [])
            .map(serialKey)
            .filter(Boolean)
    );
    if (!drop.size) return String(text ?? "");

    if (columnCount <= 1) {
        const kept = serialsFromText(text).filter(
            (serial) => !drop.has(serialKey(serial))
        );
        return serialsToText(kept);
    }

    // The same delimiter the parser detected for this paste, so the first cell
    // is the one the report named -- a line this fails to match is one the
    // parser already rejected as a column-count error, so the two cannot
    // disagree about what a row is.
    //
    // Surviving lines keep their own spacing and delimiter, which a
    // rebuild-from-parsed could not promise. NOT byte for byte, though: blank
    // lines are dropped and CRLF comes back as LF. The parser ignores blanks
    // too, so nothing changes meaning -- but a user who has just read
    // "Line 7: ..." will find the box has shifted by more than the rows
    // removed.
    const delimiter = detectPasteDelimiter(text) || "\t";

    return String(text ?? "")
        .split(/\r?\n/)
        .filter((rawLine) => {
            if (!rawLine.trim()) return false;
            const [first = ""] = rawLine.split(delimiter);
            return !drop.has(serialKey(first.trim()));
        })
        .join("\n");
};

// --------------------------------------------------------------------------
// "Check serials" -- the explicit dry run, and whether its answer still holds
// --------------------------------------------------------------------------
//
// A CHECK IS A SERVER ROUND TRIP over every serial in the box, so it is a
// button and not a debounce: firing on a typing pause means a request per
// pause, partial answers while a 200-line paste is still landing, and a form
// that feels slowest exactly when the most is being pasted.
//
// The state that matters is the STALE one. A result that no longer describes
// what is in the box reads as current, and that is worse than no result at
// all -- so the moment the box stops matching what was checked, the table and
// the tally grey out and the button renames itself.
//
// Submit is NEVER gated on this. The create call re-resolves every serial
// server-side with the same function, so skipping the button costs
// correctness nothing -- only the early warning.

export const SERIAL_CHECK_STATUS = {
    IDLE: "idle",
    CHECKING: "checking",
    FRESH: "fresh",
    STALE: "stale",
};

// The comparison key for "is the box still what was checked".
//
// Built from serialKey, so it is the same case-folded identity the backend
// resolves by: retyping "pk-1" as "PK-1" is not an edit the server would
// answer differently, and greying a good result over it would train the user
// to ignore the greying. Whitespace and ordering the parser already dropped
// are gone for the same reason -- a trailing newline is not a change.
//
// NUL-joined because no serial format in this product contains one, so two
// different lists cannot collide on one signature.
export const serialSignature = (serials = []) =>
    (Array.isArray(serials) ? serials : [])
        .map(serialKey)
        .filter(Boolean)
        .join("\u0000");

// The comparison key for "is the ROUTE still what was checked".
//
// The verdicts are not a property of the serials alone. "Forward needs a
// destination (distributor, subDistributor, dealer)" and "BAT-0001 is at
// warehouse A and cannot be forwarded from warehouse B" are both sentences
// about the route, returned per serial -- so changing the movement type or the
// destination invalidates the answer on screen exactly as editing the box
// does. Before the preview moved below the Route panel this was hard to hit;
// now it is the obvious next thing a user does after reading a refusal.
//
// Same NUL join and the same null-means-untracked contract as serialSignature.
export const routeSignature = (movement) =>
    [
        movement?.movementTypeCode ?? "",
        movement?.destinationEntityTypeCode ?? "",
        movement?.destinationEntityID ?? "",
        movement?.sourceEntityID ?? "",
    ]
        .map((part) => String(part ?? ""))
        .join("\u0000");

// Everything the serials band needs to draw itself, from three inputs.
//
// `checkedSerials` is the list the last check actually RAN AGAINST -- null
// when none has run. It is kept beside the report rather than derived from it
// because the report is keyed by serial and says nothing about what was left
// out, and because a check that FAILED (a 422) still produced a real answer
// about a real list.
export const serialCheckState = ({
    serials = [],
    checkedSerials = null,
    // The route the form shows NOW, and the one the last check ran against.
    // `checkedRoute` null means "not tracked", which is what every caller that
    // does not pass it gets -- the serials alone then decide freshness, as
    // they always did.
    route = "",
    checkedRoute = null,
    isChecking = false,
} = {}) => {
    const counted = (Array.isArray(serials) ? serials : []).filter(Boolean).length;
    const hasCheck = Array.isArray(checkedSerials);
    const lastCheckedCount = hasCheck
        ? checkedSerials.filter(Boolean).length
        : null;

    const routeMatches =
        checkedRoute === null || String(route ?? "") === String(checkedRoute);

    const matches =
        hasCheck &&
        serialSignature(serials) === serialSignature(checkedSerials) &&
        routeMatches;

    // Independent of isChecking ON PURPOSE. While a re-check is in flight the
    // previous answer is still the stale one, and it must stay greyed rather
    // than flicking back to looking current for the length of the request.
    const isStale = hasCheck && !matches;

    const status = isChecking
        ? SERIAL_CHECK_STATUS.CHECKING
        : !hasCheck
            ? SERIAL_CHECK_STATUS.IDLE
            : matches
                ? SERIAL_CHECK_STATUS.FRESH
                : SERIAL_CHECK_STATUS.STALE;

    return {
        status,
        isStale,
        counted,
        lastCheckedCount,
        // The result is DIMMED when stale, never removed: the user still has
        // to be able to read which serials the old answer covered.
        showResult: hasCheck,
        // The STALE half of the line under the box, and only that half.
        //
        // It used to also say "${counted} counted" when everything was in
        // order, which the live "N of 50" count beside it now says better --
        // two numbers for one fact on one line is how a user stops reading
        // either. What is left is the thing the live count cannot express:
        // how many rows the answer on screen actually describes.
        note: isStale ? `last previewed against ${lastCheckedCount}` : "",
        // WHY it is stale, for the caption over the dimmed table. The serials
        // and the route go stale for different reasons and the fix differs:
        // one is an edit to the box, the other a different destination.
        staleReason: !isStale
            ? ""
            : serialSignature(serials) === serialSignature(checkedSerials)
                ? "route"
                : "serials",
    };
};

// Serials that appear on more than one movement of the same work order.
//
// The backend rejects the whole request with a 400 when it sees one
// (_validateMovementShape), and a 400 is a message, not the serial table -- so
// this is checked inline before submit, where it can name the serial.
export const findDuplicateSerials = (movements = []) => {
    const counts = new Map();
    (Array.isArray(movements) ? movements : []).forEach((movement) => {
        (movement?.serials || []).forEach((serial) => {
            const key = serialKey(serial);
            if (!key) return;
            counts.set(key, (counts.get(key) || 0) + 1);
        });
    });
    const duplicates = [];
    (Array.isArray(movements) ? movements : []).forEach((movement) => {
        (movement?.serials || []).forEach((serial) => {
            const key = serialKey(serial);
            if (counts.get(key) > 1 && !duplicates.includes(serial)) {
                duplicates.push(serial);
            }
        });
    });
    return duplicates;
};

// --------------------------------------------------------------------------
// The serial report -- the structured 422 from create and 409 from approve
// --------------------------------------------------------------------------

export const SERIAL_STATUS = {
    ELIGIBLE: "eligible",
    INELIGIBLE: "ineligible",
    NEW: "new",
    DUPLICATE: "duplicate",
};

// `new` is NOT "not registered", and it is not a failure.
//
// The server emits it on exactly one branch -- `elif asset is None and
// allowsNewAssets:` in app/services/work_order.py -- so a `new` row can only
// have come from a movement whose type registers what it moves. Every other
// type turns an unknown serial into `ineligible` instead. The status therefore
// carries its own guarantee, and the client needs nothing from the config to
// read it: an unregistered serial that reached this vocabulary is going to be
// registered, which is what the type exists to do.
export const SERIAL_STATUS_LABEL = {
    [SERIAL_STATUS.ELIGIBLE]: "Ready",
    [SERIAL_STATUS.INELIGIBLE]: "Not allowed",
    [SERIAL_STATUS.NEW]: "Will be registered",
    [SERIAL_STATUS.DUPLICATE]: "Repeated",
};

// The same ramp the status chip uses, so "ready" on a serial and "approved" on
// a work order are the same green rather than two greens a shade apart.
export const SERIAL_STATUS_THEME = {
    [SERIAL_STATUS.ELIGIBLE]: chipTheme("success"),
    [SERIAL_STATUS.INELIGIBLE]: chipTheme("error"),
    // Blue, not amber: an expected outcome, not a thing to look at twice.
    [SERIAL_STATUS.NEW]: chipTheme("info"),
    [SERIAL_STATUS.DUPLICATE]: chipTheme("warning"),
};

const SERIAL_STATUS_UNKNOWN_THEME = chipTheme("neutral");

// How ONE verdict is shown, and whether it is a problem at all.
//
// TAKES NOTHING BUT THE STATUS. An earlier version resolved `new` against the
// form's movement types, on the assumption that an unregistered serial only
// means "will be registered" on a type that allows it. The server already
// guarantees that -- see the note on SERIAL_STATUS_LABEL -- so the extra
// resolution decided nothing and got one case wrong: a form mixing a
// registering movement with an ordinary one marked its `new` rows as problems,
// and "Remove these serials from the form" then stripped exactly the assets the
// movement existed to register.
//
// eligible and new are fine, ineligible and duplicate are problems, and an
// unrecognised status is shown as ITSELF rather than blanked, because a new
// value arriving is something the user should see.
export const serialStatusPresentation = (status) => ({
    label: SERIAL_STATUS_LABEL[status] || status || "-",
    theme: SERIAL_STATUS_THEME[status] || SERIAL_STATUS_UNKNOWN_THEME,
    isProblem: status !== SERIAL_STATUS.ELIGIBLE && status !== SERIAL_STATUS.NEW,
});

// summary.warnings -> the strings to show, with anything unusable dropped.
//
// The server sends an array of ready-made sentences; nothing here rewrites or
// numbers them, so a warning added on the backend reaches the screen without a
// frontend release. A non-array (an older backend, or a single string) yields
// an empty list rather than rendering "undefined" over the table.
export const serialReportWarnings = (summary) =>
    (Array.isArray(summary?.warnings) ? summary.warnings : [])
        .map((warning) => (typeof warning === "string" ? warning.trim() : ""))
        .filter(Boolean);

// The failed response -> what the table renders, or null when the response is
// an ordinary message failure.
//
// The GUARD IS THE SHAPE, via isSerialReport from the API module: create
// answers 422 and approve answers 409 with the very same body, and a caller
// that switches on the status code alone misses one of them. There is
// deliberately no pre-flight validation endpoint, so this table is the only
// place a maker ever learns that a serial is bad -- flattening it into a toast
// string is the one thing that must never happen to it.
//
// Rows are returned in the order the server reported them, which is the order
// the movements and their items were submitted in, so a maker reading down the
// table is reading down their own form.
export const summariseSerialReport = (res) => {
    if (!isSerialReport(res)) return null;

    const summary = res.data.summary || {};
    const rows = (res.data.serials || [])
        .filter(Boolean)
        .map((serial) => {
            // Label AND verdict from one place. Deriving the two separately
            // is how a row ends up reading "Will be registered" in an error
            // colour.
            const presentation = serialStatusPresentation(serial.status);
            return {
                serialNumber: serial.serialNumber ?? "",
                status: serial.status || "",
                statusLabel: presentation.label,
                assetID: serial.assetID ?? null,
                currentStateCode: serial.currentStateCode || null,
                currentEntityID: serial.currentEntityID ?? null,
                reason: serial.reason || "",
                isProblem: presentation.isProblem,
            };
        });

    const count = (key) =>
        typeof summary[key] === "number"
            ? summary[key]
            // Recomputed only when the key is missing. A summary whose numbers
            // disagree with its own rows is the server's to report, not ours to
            // quietly correct.
            : rows.filter((r) => r.status === key).length;

    return {
        summary: {
            total: typeof summary.total === "number" ? summary.total : rows.length,
            eligible: count(SERIAL_STATUS.ELIGIBLE),
            ineligible: count(SERIAL_STATUS.INELIGIBLE),
            new: count(SERIAL_STATUS.NEW),
            duplicate: count(SERIAL_STATUS.DUPLICATE),
        },
        rows,
        // What the maker has to fix. Used for the "n of m serials need
        // attention" line and to decide whether the table opens expanded.
        //
        // EXCLUDES the unregistered serials, which is the point: "Remove these
        // serials from the form" must not strip the very assets the movement
        // exists to register.
        problemSerials: rows.filter((r) => r.isProblem).map((r) => r.serialNumber),
        message: res?.responseStatus?.msg || "",
        // --- the advisory half of the report ------------------------------
        //
        // KEPT OUT OF `summary`, whose five keys are counts the chips render
        // one for one. These two are the server's ADVICE, not a verdict: a
        // request whose serials sit at several different origins is ALLOWED
        // -- one consolidation return legitimately pulls from four
        // sub-dealers -- so they are shown as a warning and never gate
        // submit. A caller that blocks on distinctOrigins > 1 has
        // misread them.
        distinctOrigins:
            typeof summary.distinctOrigins === "number" ? summary.distinctOrigins : null,
        warnings: serialReportWarnings(summary),
    };
};

// --------------------------------------------------------------------------
// The payload builder
// --------------------------------------------------------------------------

// `<input type="datetime-local">` value -> the naive UTC ISO the API wants.
//
// movementDateTime is a real date AND time: it becomes
// assetLifecycleRecord.effectiveFrom on approval, which orders the asset's
// whole history. The input is in the user's wall clock, so it goes through the
// org-timezone helper -- never `new Date(value).toISOString()`, which uses the
// BROWSER's zone and lands the movement hours out for anyone travelling or on a
// machine set to another region.
//
// Sent WITHOUT an offset: the backend stores a naive UTC datetime, and
// _toNaiveUTC() only converts an offset-aware value -- so a naive UTC string is
// the one shape that cannot be misread. The "T" is required because
// marshmallow's ISO parser is stricter than the API's own docstring suggests.
export const toMovementDateTimePayload = (localDateTime) => {
    if (!localDateTime) return null;
    // "2026-09-24T10:30" (the input's own shape) or "2026-09-24T10:30:00".
    const normalised = String(localDateTime).replace("T", " ");
    const withSeconds = normalised.length === 16 ? `${normalised}:00` : normalised;
    const utc = convertZonetoUTC(withSeconds, "YYYY-MM-DD HH:mm:ss");
    if (!utc || utc === "Invalid date") return null;
    return utc.replace(" ", "T");
};

// An ISO datetime from the API -> the string shown on screen.
//
// The format is "DD MMM YYYY hh:mm A" on purpose: the server's CSV formatter
// (convert_non_serializable) writes "%d %b %Y %I:%M %p", so the screen and the
// downloaded file render the same instant the same way rather than in two
// house styles.
export const WORK_ORDER_DATE_FORMAT = "DD MMM YYYY hh:mm A";

export const formatWorkOrderDateTime = (value, fallback = "-") => {
    if (value === null || value === undefined || value === "") return fallback;
    if (value === "0000-00-00 00:00:00") return fallback;
    const formatted = getMetricTimeStamp(value, WORK_ORDER_DATE_FORMAT);
    return formatted && formatted !== "Invalid date" ? formatted : fallback;
};

// Optional item fields are OMITTED when empty rather than sent as "".
//
// CreateWorkOrderSchema is unknown = RAISE and every optional field is
// allow_none, so null is accepted -- but an empty STRING for returnReasonCode
// or loanReferenceID is a real value the writer would store. Omitting is the
// only spelling of "the user left this blank".
const withOptional = (target, key, value) => {
    if (value === null || value === undefined || value === "") return target;
    return { ...target, [key]: value };
};

const toItemPayload = (serialNumber, movement) => {
    let item = { serialNumber: String(serialNumber).trim() };
    item = withOptional(item, "sourceEntityID", movement?.sourceEntityID);
    item = withOptional(item, "returnReasonCode", movement?.returnReasonCode);
    item = withOptional(item, "loanReferenceID", movement?.loanReferenceID);
    // Per-item overrides, keyed by the serial as the user typed it. The form
    // collects source/reason/loan per MOVEMENT because that is how a shipment is
    // assembled; itemOverrides is what lets one line differ -- a dealer
    // consolidating returns pulls from several sub-dealers in one movement.
    const override = movement?.itemOverrides?.[serialNumber] || null;
    if (override) {
        item = withOptional(item, "sourceEntityID", override.sourceEntityID);
        item = withOptional(item, "returnReasonCode", override.returnReasonCode);
        item = withOptional(item, "replacesAssetID", override.replacesAssetID);
        item = withOptional(item, "loanReferenceID", override.loanReferenceID);
        if (override.itemData && Object.keys(override.itemData).length) {
            item = { ...item, itemData: override.itemData };
        }
    }
    return item;
};

// One movement of form state -> one movement of the API payload.
//
// Carries NO documentIDs. Document upload is deferred, and CreateWorkOrderSchema
// is unknown = RAISE, so sending the key is a 400 rather than a field the server
// ignores.
export const buildMovementPayload = (movement) => {
    const items = (movement?.serials || [])
        .map((serial) => String(serial ?? "").trim())
        .filter(Boolean)
        .map((serial) => toItemPayload(serial, movement));

    let payload = {
        movementTypeCode: movement?.movementTypeCode || "",
        movementDateTime: toMovementDateTimePayload(movement?.movementDateTime),
        items,
    };
    payload = withOptional(payload, "destinationEntityID", movement?.destinationEntityID);
    payload = withOptional(payload, "completesRecordID", movement?.completesRecordID);
    return payload;
};

// The whole body for POST /api/work-orders and PUT /api/work-orders/<id>.
//
// workOrderTypeCode is included on the update too: the schema accepts it and the
// service rejects a CHANGE, so round-tripping the value the form was seeded with
// is safe and makes a mismatch fail loudly instead of silently re-typing a
// dispatch.
export const buildWorkOrderPayload = (form) => {
    let payload = {
        workOrderTypeCode: form?.workOrderTypeCode || "",
        movements: (form?.movements || []).map(buildMovementPayload),
    };
    payload = withOptional(payload, "remarks", (form?.remarks || "").trim());
    return payload;
};

// --------------------------------------------------------------------------
// How far the movement date may sit from today
// --------------------------------------------------------------------------

// Two months back, one month forward.
//
// A movement is nearly always recorded AFTER it happened -- a dispatch note
// typed up at the end of the week -- so the window is deliberately lopsided.
// The forward month exists for a shipment booked in advance; it is not a way to
// post history a quarter old, which would land assetLifecycleRecord.effectiveFrom
// behind records already written against the same asset.
export const MOVEMENT_DATE_MONTHS_BACK = 2;
export const MOVEMENT_DATE_MONTHS_FORWARD = 1;

// Two calendar dates -> the min/max an `<input type="datetime-local">` takes.
//
// The bounds are whole DAYS, so the first instant of the earliest day and the
// last minute of the latest one are both inside the window: a movement at
// 23:30 on the last allowed day is not rejected for being half an hour past a
// midnight boundary nobody told the user about.
//
// Pure, and separate from the clock, so the window can be tested without
// freezing time -- movementDateTimeBounds() below is the thin part that knows
// what day it is.
export const toDateTimeLocalBounds = (minDate, maxDate) => ({
    min: minDate ? `${minDate}T00:00` : "",
    max: maxDate ? `${maxDate}T23:59` : "",
});

// The window, resolved against the ORG's today.
//
// getRelativeMonthDate is the centralized helper and is anchored on
// getDatePickerDate() -- never `new Date()` here, which is the browser's
// calendar day and would hand a user in another region a window off by one.
//
// The window is the EDIT form's, which is the only place a date is typed. A
// create stamps the clock and never offers the field, so it cannot land
// outside this -- the bounds are still enforced there, as a backstop against a
// payload built any other way.
export const movementDateTimeBounds = () =>
    toDateTimeLocalBounds(
        getRelativeMonthDate(-MOVEMENT_DATE_MONTHS_BACK),
        getRelativeMonthDate(MOVEMENT_DATE_MONTHS_FORWARD)
    );

// Is this `<input type="datetime-local">` value inside the window?
//
// Compared as STRINGS. "YYYY-MM-DDTHH:mm" sorts lexicographically exactly as it
// sorts chronologically, both sides are in the same wall clock, and parsing
// them into Date objects would drag the browser timezone into a comparison that
// does not need it.
//
// A missing bound is not a bound: an empty min or max leaves that end open
// rather than rejecting everything.
export const isDateTimeWithinBounds = (value, bounds) => {
    if (!value) return true;
    const text = String(value);
    if (bounds?.min && text < bounds.min) return false;
    if (bounds?.max && text > bounds.max) return false;
    return true;
};

// The sentence shown when a date falls outside it. Names BOTH ends, because
// "that date is not allowed" leaves the user guessing which way to move.
export const dateBoundsMessage = (bounds) => {
    const from = (bounds?.min || "").slice(0, 10);
    const to = (bounds?.max || "").slice(0, 10);
    if (!from && !to) return "That date is not allowed";
    if (!from) return `Enter a date and time on or before ${to}`;
    if (!to) return `Enter a date and time on or after ${from}`;
    return `Enter a date and time between ${from} and ${to}`;
};

// --------------------------------------------------------------------------
// Form validation
// --------------------------------------------------------------------------

// Inline errors keyed by field path, checked before the request goes out.
//
// This is NOT a pre-flight validation of the serials -- there is no endpoint for
// that by design, and submit is where a maker learns a serial is bad. It only
// catches what the client can know on its own: a missing type, a movement with
// no date, no destination where the movement type requires one, no serials at
// all, and a serial repeated across movements.
export const validateWorkOrderForm = (
    form,
    movementTypesByCode = {},
    // Optional. The input's own min/max only MARKS a value out of range -- the
    // form is not submitted natively, so nothing stops a typed date outside the
    // window from reaching the API. This is the half that does.
    { dateBounds = null } = {}
) => {
    const errors = {};
    if (!form?.workOrderTypeCode) {
        errors.workOrderTypeCode = "Choose a work order type";
    }

    // REQUIRED, on create and on edit. The reviewer has nothing else to go on
    // -- the movement says what, never why -- and the schema enforces
    // Length(min=1), so a blank one is a 400 rather than an empty column.
    //
    // TRIMMED before the test, because "   " satisfies min=1 on the wire and
    // says nothing to a human. The two ends have to agree on what empty means.
    if (!String(form?.remarks ?? "").trim()) {
        errors.remarks = "Tell the reviewer why this is being raised";
    }

    const movements = form?.movements || [];
    if (!movements.length) {
        errors.movements = "Add at least one movement";
    }

    movements.forEach((movement, index) => {
        if (!movement?.movementTypeCode) {
            errors[`movements.${index}.movementTypeCode`] = "Choose a movement type";
        }
        if (!movement?.movementDateTime) {
            errors[`movements.${index}.movementDateTime`] =
                "Enter the date and time of the movement";
        } else if (!toMovementDateTimePayload(movement.movementDateTime)) {
            errors[`movements.${index}.movementDateTime`] =
                "That date and time could not be read";
        } else if (
            dateBounds &&
            !isDateTimeWithinBounds(movement.movementDateTime, dateBounds)
        ) {
            errors[`movements.${index}.movementDateTime`] = dateBoundsMessage(dateBounds);
        }
        if (!(movement?.serials || []).length) {
            errors[`movements.${index}.serials`] = "Add at least one serial number";
        }
        // Only where the movement type declares destinations. A type with an
        // empty destinationEntityTypes accepts none, so demanding one here would
        // block a movement the backend would have taken.
        const movementType = movementTypesByCode?.[movement?.movementTypeCode];
        if (
            movementType?.destinationEntityTypes?.length &&
            !movement?.destinationEntityID
        ) {
            errors[`movements.${index}.destinationEntityID`] =
                "Choose a destination";
        }
    });

    const duplicates = findDuplicateSerials(movements);
    if (duplicates.length) {
        errors.duplicateSerials = `These serials appear on more than one movement: ${duplicates.join(", ")}`;
    }

    return errors;
};

// --------------------------------------------------------------------------
// CSV parity
// --------------------------------------------------------------------------

// The visible columns, as {dataIndex, headerLabel}, for the export.
//
// THERE IS NO CLIENT-SIDE ROW TRANSFORM on this page, and so nothing to keep in
// step: the server renames the columns, localises the dates and derives
// statusLabel, and the screen renders that same statusLabel verbatim. The
// download and the emailed attachment are therefore the file on screen by
// construction rather than by two formatters agreeing.
//
// NO SENTINEL COLUMN. An earlier version asked for reviewAction under a fake
// header so the client could derive the label itself. That is dropped -- and it
// must stay dropped for `mail` in particular: convertDataForCSV applies
// columnOrder AFTER renaming to headerLabel and the server writes the file
// itself, so a sentinel would have arrived in the recipient's inbox as a
// literal "__reviewAction" column. Only send columns that belong in the file.
export const buildCSVColumnsPayload = (columns = []) =>
    (columns || [])
        .filter((c) => c?.dataIndex && c?.valueRenderingType !== "action-menu")
        .map((c) => ({
            dataIndex: c.dataIndex,
            headerLabel:
                typeof c.headerLabel === "string"
                    ? c.headerLabel
                    : typeof c.title === "string"
                        ? c.title
                        : c.dataIndex,
        }));

// --------------------------------------------------------------------------
// Narrowing the list
// --------------------------------------------------------------------------

export const WORK_ORDER_FILTER_KEY_TYPE = "workOrderTypeCode";

// Panel selection + the header checkbox -> request params.
//
// ONE mapping, fed to all THREE places the narrowing has to reach: the list,
// /api/work-orders/counts and the list-action export. A filter applied to the
// table but not to the counts leaves the queue badges contradicting the rows;
// not applied to the export gives a download that disagrees with the screen.
//
// The type travels as an ARRAY, whole: workOrderTypeCode takes one or many
// values under the same param name and the query layer uses IN. The encoding
// -- comma separated on a query string, a real array in a POST body -- belongs
// to the transport and is applied once, in buildWorkOrderParams.
//
// createdByMe comes in beside the panel selection rather than through it: it is
// a BOOLEAN, no displayType renders one, and the identity it resolves to is
// taken from the token in the service -- a request cannot name whose work
// orders it wants. It stays a header control for that reason, and is mapped
// here so that both controls still produce a single set of params and so a
// single state update.
//
// `status` is not here and must not be: the Requests / History tabs are the
// status filter (see the tab strip in WorkOrdersTabContent).
export const mapWorkOrderFiltersToParams = (
    selectedFilters = {},
    { createdByMe = false } = {}
) => {
    const params = {};

    const workOrderTypeCodes = selectedValues(
        selectedFilters,
        WORK_ORDER_FILTER_KEY_TYPE
    );
    if (workOrderTypeCodes.length) params.workOrderTypeCode = workOrderTypeCodes;

    // Only when true. buildWorkOrderParams drops a false, and sending
    // `createdByMe=false` explicitly would serialise as the string "false".
    if (createdByMe) params.createdByMe = true;

    return params;
};

// --------------------------------------------------------------------------
// Config lookups
// --------------------------------------------------------------------------

export const indexByCode = (rows = []) =>
    (Array.isArray(rows) ? rows : []).reduce((acc, row) => {
        if (row?.code) acc[row.code] = row;
        return acc;
    }, {});

// The entity type codes a movement type allows on each end.
//
// Both lists come from the movementType row and the create service enforces the
// same ones, so a picker narrowed by them cannot offer a choice the write will
// refuse. An EMPTY list means "no entity of any type belongs here" -- not "any
// entity" -- which is why the caller renders no picker at all rather than an
// unfiltered one.
export const allowedEntityTypes = (movementType, end) => {
    const key = end === "source" ? "sourceEntityTypes" : "destinationEntityTypes";
    const list = movementType?.[key];
    return Array.isArray(list) ? list.filter(Boolean) : [];
};

// The helper text under the movement type picker.
//
// The type's own `description` is the config's per-type guidance and is shown
// verbatim. allowsNewAssets is appended rather than left to be discovered from
// the serial report: it is the one property that changes what a serial the
// registry has never seen MEANS, and a maker should know before submitting that
// a typo will be registered as an asset rather than refused.
export const movementTypeGuidance = (movementType) => {
    if (!movementType) return "";
    const parts = [];
    const description =
        typeof movementType.description === "string"
            ? movementType.description.trim()
            : "";
    if (description) parts.push(description);
    if (movementType.allowsNewAssets === true) {
        parts.push(
            "Serials that are not in the registry yet will be registered by this movement."
        );
    }
    return parts.join(" ");
};

// --------------------------------------------------------------------------
// Which work order type this form raises
// --------------------------------------------------------------------------

// The form no longer ASKS. Every work order raised from this module is an
// asset movement, and a required dropdown with one possible answer is a
// question the form already knows the answer to.
//
// The field did not go anywhere -- it is still on the payload and it is still
// what the per-type permission is granted on. Only the control went. So the
// code is RESOLVED from the config list the user is actually allowed to raise
// (workOrderFormAccess().types), never written into JSX:
//
//   - editing an existing request  -> the type already on it, always. The
//     service rejects a change, and re-typing a dispatch as something
//     harmless is exactly the escalation the per-type grant exists to stop.
//   - one permitted type           -> that one.
//   - several                      -> the movement one, matched on the code
//     or the label. 'ASSET_MOVEMENT' and 'PACK_REPLACEMENT' are both seeded
//     (app/database/asset/seed_movement_config.sql) and this page raises the
//     first; the match is a substring rather than an equality so an org that
//     seeded 'ASSET_MOVEMENT_V2' still resolves.
//   - none                         -> "", and workOrderFormAccess has already
//     closed the form with its denial state.
export const resolveWorkOrderTypeCode = ({ types = [], workOrder = null } = {}) => {
    if (workOrder?.workOrderTypeCode) return workOrder.workOrderTypeCode;

    const list = (Array.isArray(types) ? types : []).filter((type) => type?.code);
    if (!list.length) return "";
    if (list.length === 1) return list[0].code;

    const movement = list.find(
        (type) =>
            /movement/i.test(String(type.code)) ||
            /movement/i.test(String(type.label || ""))
    );
    return (movement || list[0]).code;
};

// The work order type's LABEL, for the page heading.
//
// The form does not ask for the type -- every work order raised from this
// module is one kind, and a required dropdown with one possible answer is a
// question the form already knows the answer to. But the heading should still
// SAY which kind: "New Work Order" alone does not distinguish this page from
// the one a second type would open, and the maker is about to raise something
// that will sit in a queue beside other types.
//
// Read from the SAME config row the code is resolved from, so the heading and
// the payload can never name different types.
//
// The humanised code is a fallback and not a formatter: a type whose config
// row carries no label is a configuration gap, and "ASSET_MOVEMENT" shouted in
// a heading is worse than "Asset Movement" while still being obviously the
// code rather than a written label.
export const humaniseTypeCode = (code = "") =>
    String(code || "")
        .split(/[_\s]+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");

export const resolveWorkOrderTypeLabel = ({ types = [], workOrder = null } = {}) => {
    const code = resolveWorkOrderTypeCode({ types, workOrder });
    if (!code) return "";
    const match = (Array.isArray(types) ? types : []).find(
        (type) => type?.code === code
    );
    return match?.label || humaniseTypeCode(code);
};

// --------------------------------------------------------------------------
// Where the assets are coming FROM
// --------------------------------------------------------------------------

export const SOURCE_CONTROL = {
    NONE: "none",
    PICKER: "picker",
    READOUT: "readout",
};

// Is Source something the maker CHOOSES, or something the form reads back?
//
// THE ARGUMENT THIS SETTLES. A picker cannot be right for a movement of
// existing assets: an asset is where it is, and asking a maker to declare it
// invites a request that contradicts the record and is refused on save. So
// Source is a read-out that fills in from the resolved serials.
//
// The one exception is a movement type that REGISTERS what it moves --
// allowsNewAssets -- because the assets do not exist yet, there is nothing to
// read them back from, and the vendor genuinely is a choice. The config
// already says which case we are in, so nothing here needs to know the name
// of a movement type.
//
// NO MOVEMENT TYPE CHOSEN YET is the read-out in its empty state, not a
// picker: an input that appears speculatively and then vanishes when the type
// is picked is worse than a line of text that fills in.
//
// An empty sourceEntityTypes means "no entity belongs on this end", which is
// NOT "any entity" -- the caller renders nothing at all rather than an
// unfiltered picker.
export const sourceControlMode = (movementType) => {
    if (!movementType) return SOURCE_CONTROL.READOUT;
    if (movementType.allowsNewAssets === true) {
        return allowedEntityTypes(movementType, "source").length
            ? SOURCE_CONTROL.PICKER
            : SOURCE_CONTROL.NONE;
    }
    return SOURCE_CONTROL.READOUT;
};

// entityState from useWorkOrderEntities -> {id: name}.
//
// The serial report names custody by ID only (currentEntityID), and the
// entity LISTS are already in hand for the destination picker, so the read-out
// borrows them rather than adding a lookup endpoint. An id the lists do not
// cover -- an entity past the first page, or of a type this movement does not
// allow on either end -- falls back to its id, which is still an answer.
export const entityNamesByID = (entityState = {}) => {
    const names = {};
    Object.values(entityState || {}).forEach((bucket) => {
        (bucket?.records || []).forEach((entity) => {
            if (entity?.id === null || entity?.id === undefined) return;
            names[String(entity.id)] =
                entity.name || entity.entityCode || `#${entity.id}`;
        });
    });
    return names;
};

// The serial report -> the sentence in the From half of the Route panel.
//
// DETECTED, NOT CHOSEN. Several origins in one request is ALLOWED and stays
// one request -- a consolidation return legitimately pulls from four
// sub-dealers -- so "mixed" is a statement of fact and never an error.
export const summariseRouteSource = (report, entityNames = {}) => {
    const rows = report?.rows || [];
    if (!rows.length) {
        return {
            status: "unknown",
            label: "",
            detail: "Check the serials to read this from the registry",
            entityIDs: [],
        };
    }

    const held = rows.filter(
        (row) => row?.currentEntityID !== null && row?.currentEntityID !== undefined
    );
    const entityIDs = [...new Set(held.map((row) => String(row.currentEntityID)))];

    // No custody at all. A serial a registering movement is about to create
    // has never been anywhere, which is the answer rather than a gap.
    if (!entityIDs.length) {
        return {
            status: "none",
            label: "Not held by anyone yet",
            detail: `nothing has moved ${rows.length === 1 ? "it" : "them"} yet`,
            entityIDs: [],
        };
    }

    if (entityIDs.length === 1) {
        const id = entityIDs[0];
        return {
            status: "single",
            label: entityNames?.[id] || `Entity #${id}`,
            detail:
                held.length === rows.length
                    ? `all ${rows.length} serial${rows.length === 1 ? "" : "s"}`
                    : `${held.length} of ${rows.length} serials`,
            entityIDs,
        };
    }

    return {
        status: "mixed",
        label: `${entityIDs.length} locations`,
        detail: `these serials are held in ${entityIDs.length} different places, which is allowed`,
        entityIDs,
    };
};

// --------------------------------------------------------------------------
// Opening the form with serials already in it
// --------------------------------------------------------------------------

// react-router location.state -> the serials the box opens with.
//
// STATE AND NOT THE QUERY STRING. A selection on the Assets tab can be two
// hundred serials, which is a URL no browser, proxy or log line should be
// asked to carry -- and it would be shared and bookmarked as though it meant
// something.
//
// EVERY branch returns a list, including the one where there is no state at
// all: the page is reachable directly, by refresh and from the module's
// floating button, and all three of those are a blank form rather than a
// broken one.
export const prefillSerialsFromState = (state) => {
    if (!state || typeof state !== "object") return [];
    const raw = state.serials;
    if (typeof raw === "string") return serialsFromText(raw);
    if (!Array.isArray(raw)) return [];
    // Through the same parser as a paste, so the dedupe, the trimming and the
    // case-folded identity are one implementation rather than two.
    return serialsFromText(
        raw
            .map((serial) => String(serial ?? "").trim())
            .filter(Boolean)
            .join("\n")
    );
};

// --------------------------------------------------------------------------
// The date control's value shape
// --------------------------------------------------------------------------
//
// The form still holds "YYYY-MM-DDTHH:mm" -- the shape the bounds are
// expressed in (toDateTimeLocalBounds), the shape isDateTimeWithinBounds
// compares as strings, and the shape toMovementDateTimePayload reads. The
// native datetime-local input that shape was named after is gone; these two
// functions are the whole of the boundary with the product's own picker,
// which works in "YYYY-MM-DD HH:mm:ss".

export const toPickerDateTime = (value) => {
    if (!value) return null;
    const text = String(value).replace("T", " ");
    return text.length === 16 ? `${text}:00` : text;
};

// Truncated to the MINUTE on the way back. The bounds carry no seconds, and
// isDateTimeWithinBounds compares lexicographically -- so a value of
// "...T23:59:30" would sort past a max of "...T23:59" and be rejected for
// thirty seconds nobody typed.
export const fromPickerDateTime = (value) => {
    if (!value) return "";
    return String(value).replace(" ", "T").slice(0, 16);
};

// --------------------------------------------------------------------------
// What "Check serials" needs before it can ask
// --------------------------------------------------------------------------

// The field paths the dry run actually depends on.
//
// A CHECK NEEDS A COHERENT MOVEMENT, NOT A COMPLETE REQUEST. The destination
// decides nothing about where a serial currently IS, so demanding one before
// the button works would make the user finish the form to find out whether the
// form was worth finishing. The movement TYPE is the opposite case and is
// required: it is what turns an unregistered serial into "will be registered"
// rather than "not allowed", so a check run without one would answer a
// different question from the one the submit will ask.
//
// The date is required because the create schema requires it -- the dry run is
// the same endpoint and the same schema, so a payload without one is a 400
// rather than a report.
const SERIAL_CHECK_FIELDS = ["movementTypeCode", "movementDateTime", "serials"];

// Derived from validateWorkOrderForm rather than re-implemented: two copies of
// a rule drift, and the copy that drifts is the one nobody is looking at.
export const validateSerialCheck = (form, movementTypesByCode = {}, options = {}) => {
    const all = validateWorkOrderForm(form, movementTypesByCode, options);
    return Object.fromEntries(
        Object.entries(all).filter(
            ([key]) =>
                key === "duplicateSerials" ||
                key === "movements" ||
                SERIAL_CHECK_FIELDS.some((field) => key.endsWith(`.${field}`))
        )
    );
};

// The FROM half of the Route panel, in every state it has.
//
// It MIRRORS the TO half, which was already right: with no movement type
// chosen, TO says "choose a movement type" and FROM used to say "Check the
// serials to read this from the registry" -- pointing the user at a step that
// is not the next one, in vocabulary ("read this from the registry") that is
// ours and not theirs. The two ends of one panel cannot be in different
// tenses.
//
// Four states, and they are ordered by what the user has actually done:
//
//   noType    nothing chosen yet          -> the same prompt TO gives
//   picker    the type registers assets   -> a real choice, see sourceControlMode
//   unchecked type chosen, not yet checked-> what WILL happen, in plain words
//   resolved  checked                     -> the holder, from the report
//
// The helper line is withheld until a type is chosen: "an asset can only move
// from where it actually is" is an explanation of a read-out that is not
// showing anything yet.
export const SOURCE_READOUT_HELPER =
    "Detected, not chosen. An asset can only move from where it actually is.";

export const sourceReadoutState = ({
    movementType = null,
    report = null,
    entityNames = {},
} = {}) => {
    if (!movementType) {
        return {
            status: "noType",
            label: "",
            detail: "Choose a movement type to see where these assets come from.",
            helper: "",
            entityIDs: [],
        };
    }

    const custody = summariseRouteSource(report, entityNames);

    // No answer yet. Said as a promise about the serials box rather than as an
    // instruction: the next step is to type the serials, and checking them is
    // optional -- submit re-resolves everything anyway.
    if (custody.status === "unknown") {
        return {
            status: "unchecked",
            label: "",
            detail: "Taken from the serials once you check them",
            helper: SOURCE_READOUT_HELPER,
            entityIDs: [],
        };
    }

    return { ...custody, helper: SOURCE_READOUT_HELPER };
};

// --------------------------------------------------------------------------
// Where Back goes
// --------------------------------------------------------------------------

// The module's own tab vocabulary, and the name each slug is shown under.
//
// A LAST RESORT, not the source of truth. tabsConfig names are per-org config
// and the caller carries the real one across in router state -- see
// originLabel below. These three exist because the DEFAULT destination has
// nobody to carry a name for it: arriving by the floating button or by URL
// there is no origin at all, and the button still has to say where it goes.
export const ASSET_MANAGEMENT_TAB_LABELS = {
    network: "Network",
    assets: "Assets",
    "work-orders": "Work Orders",
};

// THE QUEUE, not just the module. /asset-management alone lands on the first
// visible tab -- Network for most users -- so a maker who had just raised a
// request never saw the row they created.
export const DEFAULT_RETURN_TAB = "work-orders";

export const assetManagementTabPath = (slug) =>
    `/asset-management?tab=${slug || DEFAULT_RETURN_TAB}`;

// Back, Cancel and the breadcrumb all resolve through here.
//
// WHY AN ORIGIN IS CARRIED AT ALL. Ticking three serials on the Assets tab and
// pressing "+ New Work Order" used to strand the user on the Work Orders queue
// when they backed out -- a different tab from the one they were reading,
// with their selection gone and no way back to it. Back has to return where
// the user came from.
//
// THE DEFAULT IS STILL THE QUEUE, and deliberately: with no origin the user
// arrived by the floating button or by URL, and the queue is where a raised
// request appears.
//
// `allowedSlugs` is the caller's view of what this user may actually open. An
// origin naming a tab they cannot see -- stale state, a changed role, a forged
// history entry -- FALLS BACK rather than resolving to nothing, because the
// one thing a Back button may never do is have no destination. Passing null
// skips the check, which is the right behaviour for a caller that does not
// know: the destination validates the slug again on arrival
// (resolveRequestedTab) and lands on the first visible tab if it has to.
//
// The LABEL follows the destination. It cannot say "Back to Work Orders" while
// going to Assets, which is the bug that made this function necessary.
// BACK MEANS BACK.
//
// Three ways out, in order, and the first that exists wins:
//
//   1. HISTORY. If this page was reached from somewhere inside the app, the
//      previous entry is where the user actually was -- which after the mode
//      dialog is the Assets tab they ticked rows on, not a tab slug. Going
//      to a fixed destination instead lands them somewhere they have never
//      been and loses whatever state that screen held.
//   2. THE ORIGIN carried in router state, for a page opened from a link or
//      reached after a redirect, where history points outside the app.
//   3. The Work Orders queue, because a raised request appears there.
//
// `historyLength` and `hasOrigin` are passed in rather than read from the
// window, so the decision is testable without a browser.
//
// A SUCCESSFUL SAVE uses the same resolution with `afterSave`, which refuses
// history: the form is still mounted in the entry the user would go back TO,
// and replaying it would return them to the form they just submitted.
export const resolveWorkOrderExit = ({
    state = null,
    allowedSlugs = null,
    historyLength = 0,
    afterSave = false,
} = {}) => {
    const destination = resolveWorkOrderReturn(state, { allowedSlugs });

    // > 1 means this tab has an entry to go back to. A fresh tab opened on
    // this URL has exactly 1 and must not call history.back(), which would
    // leave the user on a blank tab.
    const canGoBack = !afterSave && Number(historyLength) > 1;

    return { ...destination, useHistory: canGoBack };
};

export const resolveWorkOrderReturn = (state, { allowedSlugs = null } = {}) => {
    const requested =
        state && typeof state === "object" && typeof state.originTab === "string"
            ? state.originTab
            : "";

    const known = Object.prototype.hasOwnProperty.call(
        ASSET_MANAGEMENT_TAB_LABELS,
        requested
    );
    const permitted =
        !Array.isArray(allowedSlugs) || allowedSlugs.includes(requested);

    const useOrigin = Boolean(requested) && known && permitted;
    const slug = useOrigin ? requested : DEFAULT_RETURN_TAB;

    // The org's own name for the tab, carried over by whoever navigated, so a
    // renamed tab does not get called by the default name on the way back.
    // Only honoured for the origin -- a label without its slug describes
    // nothing.
    const carried =
        useOrigin && typeof state?.originLabel === "string"
            ? state.originLabel.trim()
            : "";

    return {
        slug,
        path: assetManagementTabPath(slug),
        label: carried || ASSET_MANAGEMENT_TAB_LABELS[slug],
    };
};

// --------------------------------------------------------------------------
// The paste: one row per asset, and how many columns that row has
// --------------------------------------------------------------------------
//
// THE SHAPE COMES FROM THE MOVEMENT TYPE, not from a global setting. A Forward
// moves assets that already exist, so a serial is the whole row. A Vendor
// Intake CREATES them, so the paste is what brings them into being and it has
// to carry what the asset is: name and SKU. Those two ride as
// assetMovementItem.itemData and are copied onto the created asset by
// _registrationAttributes, because they describe the thing and not the
// movement -- a battery keeps its name and SKU after it has moved.
//
// EXACTLY itemName and sku. REGISTRATION_ATTRIBUTE_KEYS in
// app/services/work_order.py reads those two and ignores everything else, so a
// third column added here would be collected, sent, and silently dropped.

// app/requests/work_order_requests.py::MAX_ITEMS_PER_MOVEMENT. Stated to the
// user BEFORE they paste and enforced before the request goes out -- the whole
// point is that nobody pastes two hundred rows and learns the limit from a
// server error.
export const MAX_ITEMS_PER_PASTE = 50;

const COLUMN = {
    serialNumber: { key: "serialNumber", label: "Serial Number" },
    itemName: { key: "itemName", label: "Item Name" },
    sku: { key: "sku", label: "SKU" },
    // A REAL per-item column on assetMovementItem, not an attribute. It does
    // not go near itemData -- see movementItemOverrides.
    loanReferenceID: { key: "loanReferenceID", label: "Loan ID" },
};

// One example value per column, so the hint above the box is built from the
// same list the parser reads and the two can never describe different shapes.
const SAMPLE = {
    serialNumber: "BAT-51105-B-130455",
    itemName: "48V 105Ah Battery Pack",
    sku: "STD-51V-105AH",
    loanReferenceID: "LN-88120",
};

// networkEntityMeta.entityTypeCode, NOT a movement type name.
//
// The distinction matters and is the rule this module has held throughout:
// nothing here knows that a movement is called "delivery". What it knows is
// that a movement whose destination can be a CUSTOMER is one where each
// battery goes to a person, and a person's loan is per battery. That is a
// property of the entity type, which is config, and it stays true for any
// org that adds a second movement type ending at a customer.
export const CUSTOMER_ENTITY_TYPE_CODE = "customer";

export const hasCustomerDestination = (movementType) =>
    allowedEntityTypes(movementType, "destination").includes(
        CUSTOMER_ENTITY_TYPE_CODE
    );

// The paste's shape, COMPOSED from what the movement type actually does.
//
// Two independent questions, asked separately rather than switched on a
// movement type:
//
//   allowsNewAssets            -> the assets do not exist yet, so the paste
//                                 has to say what they are: item name and SKU.
//   customer destination       -> each battery goes to one person, so each row
//                                 carries its own Loan ID.
//
// Composing them rather than picking one of three fixed shapes means a
// movement that does both -- an org that registers straight into a customer's
// hands -- gets four columns without anybody adding a fourth constant. And a
// movement that does neither gets the bare serial, which is every ordinary
// movement of assets that already exist.
//
// The CUSTOMER is not a column. It is chosen once, in the destination picker,
// for the whole order: a delivery run goes to one person, and repeating their
// name on fifty rows is fifty chances to spell it differently.
export const movementPasteSpec = (movementType) => {
    const carriesItemData = movementType?.allowsNewAssets === true;
    const carriesLoanReference = hasCustomerDestination(movementType);

    const columns = [COLUMN.serialNumber];
    if (carriesItemData) columns.push(COLUMN.itemName, COLUMN.sku);
    if (carriesLoanReference) columns.push(COLUMN.loanReferenceID);

    return {
        columns,
        sample: columns.map((column) => SAMPLE[column.key]),
        carriesItemData,
        carriesLoanReference,
    };
};

// PER PASTE, not per line.
//
// A block copied out of Excel is tab separated; a file export is comma
// separated. One body containing both is a BAD PASTE rather than two formats,
// so one delimiter is chosen for the whole thing and the rows that disagree
// with it surface as wrong-column-count errors on their own line -- which
// names the problem far better than a global "mixed separators" would.
//
// Tab wins when both appear: a tabbed body with a comma in it is a spreadsheet
// paste containing a comma in a product name, which is ordinary. A comma body
// containing a stray tab is not a thing that happens.
export const detectPasteDelimiter = (text) => {
    const body = String(text ?? "");
    if (body.includes("\t")) return "\t";
    if (body.includes(",")) return ",";
    return null;
};

// Is this first row the column headers rather than an asset?
//
// Skipped rather than rejected. Copying the header with the data is the single
// most likely mistake anyone will make here, and refusing the paste over it
// teaches nothing -- the instruction above the box already says not to, and
// this is what happens when it is missed anyway.
export const isHeaderRow = (cells, columns) => {
    const values = (Array.isArray(cells) ? cells : []).map((cell) =>
        String(cell ?? "").trim().toLowerCase()
    );
    const expected = (columns || []).map((column) => column.label.toLowerCase());
    if (values.length !== expected.length) return false;
    return expected.every((label, index) => values[index] === label);
};

// Within ONE line of a single-column paste.
//
// COMMA AND SEMICOLON ONLY, and the absence of the tab is the point.
//
// A comma on a one-column line genuinely means several serials -- "A-1, A-2,
// A-3" is how people write a short list -- and that stays. A TAB never does.
// A tab is what a spreadsheet puts BETWEEN COLUMNS, so a tab on a line the
// spec says has one column means the paste has more columns than the movement
// wants, which is a column-count error.
//
// Splitting on it instead invented serials: pasting delivery-shaped data
// (`BAT-000001<tab>LOAN001`) into a Forward produced two serials and reported
// the loan id as an unregistered asset. The shape was wrong and nothing said
// so.
//
// Multi-column pastes never use this at all -- there the delimiter IS the
// column boundary, and splitting on anything else would tear a product name
// containing a semicolon into two columns.
const SINGLE_COLUMN_SEPARATORS = /[,;]+/;

const columnNames = (columns) => (columns || []).map((column) => column.label).join(", ");

// The box -> rows, errors, and everything the band needs to describe itself.
//
// ERRORS ARE PER LINE and never silent. A row with the wrong number of values
// is reported at its own line number, because "3 rows could not be read" in a
// 50-row paste is not something anyone can act on.
//
// Line numbers are 1-BASED and count blank lines, so they match what the user
// sees in the box rather than an index into the rows we kept.
export const parsePastedRows = (
    text,
    // Defaulted to the bare-serial reading, which is what every movement of
    // assets that already exist wants. movementPasteSpec(null) IS that
    // reading, so there is one definition of it rather than a constant beside
    // the function that builds it.
    spec = null,
    { maxRows = MAX_ITEMS_PER_PASTE } = {}
) => {
    const columns = spec?.columns || movementPasteSpec(null).columns;
    const multiColumn = columns.length > 1;
    const lines = String(text ?? "").split(/\r?\n/);

    const rows = [];
    const errors = [];
    const seen = new Set();
    let headerSkipped = false;
    let firstContentSeen = false;

    // One delimiter for the whole paste. A multi-column spec with no
    // separator anywhere still gets one, so every line reports "expected 3,
    // found 1" rather than the parser quietly deciding it is single column.
    const detected = detectPasteDelimiter(text);
    const delimiter = multiColumn ? detected || "\t" : detected;

    const addRow = (line, values) => {
        const serialNumber = values.serialNumber;
        const key = serialKey(serialNumber);
        if (seen.has(key)) {
            // An ERROR in multi-column mode and a silent drop in single: two
            // intake rows for one serial carry two different item names and
            // there is no honest way to pick one, whereas two bare serials are
            // the same asset said twice and nothing is lost by keeping one.
            if (multiColumn) {
                errors.push({
                    line,
                    message: `Line ${line}: "${serialNumber}" appears more than once`,
                });
            }
            return;
        }
        seen.add(key);
        rows.push({ line, serialNumber, values });
    };

    lines.forEach((rawLine, index) => {
        const line = index + 1;
        const trimmed = rawLine.trim();
        if (!trimmed) return;

        if (multiColumn) {
            // Split the RAW line, then trim each cell -- never the other way
            // round. Trimming first eats the trailing separators, so
            // "A-1<tab><tab>" would arrive as one cell and be reported as a
            // shifted column when it is really two blanks. Two separators in a
            // row are a blank, which is a different error with a different fix.
            const cells = rawLine.split(delimiter).map((cell) => cell.trim());

            if (!firstContentSeen && isHeaderRow(cells, columns)) {
                firstContentSeen = true;
                headerSkipped = true;
                return;
            }
            firstContentSeen = true;

            if (cells.length !== columns.length) {
                errors.push({
                    line,
                    message: `Line ${line}: expected ${columns.length} values (${columnNames(columns)}), found ${cells.length}`,
                });
                return;
            }

            const blanks = columns
                .filter((column, position) => !cells[position])
                .map((column) => column.label);
            if (blanks.length) {
                errors.push({
                    line,
                    message: `Line ${line}: ${blanks.join(" and ")} ${blanks.length === 1 ? "is" : "are"} required`,
                });
                return;
            }

            const values = {};
            columns.forEach((column, position) => {
                values[column.key] = cells[position];
            });
            addRow(line, values);
            return;
        }

        // Single column. A header of its own is skipped for the same reason.
        if (!firstContentSeen && isHeaderRow([trimmed], columns)) {
            firstContentSeen = true;
            headerSkipped = true;
            return;
        }
        firstContentSeen = true;

        // A TAB on a one-column line is a COLUMN, not a separator. Reported
        // at its own line with the count, exactly as the multi-column branch
        // reports a row of the wrong width -- because that is what it is.
        if (rawLine.includes("\t")) {
            const cells = rawLine.split("\t").map((cell) => cell.trim());
            errors.push({
                line,
                message: `Line ${line}: expected ${columns.length} value (${columnNames(columns)}), found ${cells.length}`,
            });
            return;
        }

        // A line may still hold several serials -- "One serial per line, or
        // comma separated" has always been true of this box.
        trimmed
            .split(SINGLE_COLUMN_SEPARATORS)
            .map((value) => value.trim())
            .filter(Boolean)
            .forEach((serialNumber) => addRow(line, { serialNumber }));
    });

    return {
        columns,
        // Both capability flags travel with the parse, because everything
        // downstream -- the overrides builder, the preview columns -- reads
        // the PARSE and never the spec it came from.
        carriesItemData: Boolean(spec?.carriesItemData),
        carriesLoanReference: Boolean(spec?.carriesLoanReference),
        delimiter,
        headerSkipped,
        rows,
        errors,
        count: rows.length,
        maxRows,
        // Counted over what PARSED, so a paste of 60 rows where 15 are
        // malformed does not also claim to be over the limit.
        capExceeded: rows.length > maxRows,
    };
};

// The one message that blocks Preview and Submit, or "" when nothing does.
//
// THE CAP IS NAMED FIRST. It is the failure the whole limit exists to prevent
// being discovered late, and a user who pasted two hundred rows does not need
// to be told about line 7 as well.
export const pasteValidationError = (parsed) => {
    if (!parsed) return "";
    if (parsed.capExceeded) {
        return `${parsed.count} rows pasted. Up to ${parsed.maxRows} can go on one work order — remove ${parsed.count - parsed.maxRows}.`;
    }
    if (parsed.errors?.length) {
        const count = parsed.errors.length;
        return `${count} ${count === 1 ? "row" : "rows"} could not be read. Fix ${count === 1 ? "it" : "them"} and preview again.`;
    }
    return "";
};

// Parsed rows -> the per-item payload the existing builder already knows how
// to send.
//
// itemOverrides is NOT a new mechanism: buildMovementPayload has always fanned
// per-movement values across items and let itemOverrides differ one of them,
// and `itemData` is already one of the keys it copies. So the three-column
// paste reaches the API through the path that was already there, and
// buildMovementPayload is untouched.
//
// Keyed by the serial AS TYPED, because that is the key toItemPayload looks
// the override up by.
export const movementItemOverrides = (parsed) => {
    const wantsItemData = Boolean(parsed?.carriesItemData);
    const wantsLoanReference = Boolean(parsed?.carriesLoanReference);
    if (!wantsItemData && !wantsLoanReference) return {};

    return (parsed.rows || []).reduce((overrides, row) => {
        const override = {};

        // ATTRIBUTES. itemName and sku have no column of their own -- they
        // ride in the itemData JSON and are copied onto the created asset.
        if (wantsItemData) {
            const itemData = {};
            if (row.values?.itemName) itemData.itemName = row.values.itemName;
            if (row.values?.sku) itemData.sku = row.values.sku;
            if (Object.keys(itemData).length) override.itemData = itemData;
        }

        // A REAL COLUMN, and deliberately not beside them.
        // assetMovementItem.loanReferenceID exists, is queried and is shown on
        // the asset; putting it in itemData would hide a first-class field
        // inside a JSON blob that cannot be indexed.
        if (wantsLoanReference && row.values?.loanReferenceID) {
            override.loanReferenceID = row.values.loanReferenceID;
        }

        if (!Object.keys(override).length) return overrides;
        return { ...overrides, [row.serialNumber]: override };
    }, {});
};

// The preview posts the SAME payload a create posts. remarks is exempt from
// the required rule when validateOnly is set (evcell 832bcb3a), so there is
// nothing to substitute -- an unwritten remark stays unwritten and the server
// answers the question actually asked.
//
// This replaced a "(preview)" placeholder sent to satisfy a validator that
// should not have applied. If a preview ever starts failing on remarks again,
// the bug is the backend exemption, not this function.
export const buildPreviewPayload = (form) => buildWorkOrderPayload(form);

// --------------------------------------------------------------------------
// The preview table: showing the user what they actually pasted
// --------------------------------------------------------------------------

// The columns the preview grows BEYOND the serial, or none.
//
// The whole point of previewing a three-column paste is confirming the columns
// landed in the right places: a row reading only "BAT-000002" does not tell
// anyone that ITEM001 became the item name and SKU001 the SKU, which is
// exactly what a shifted column gets wrong and exactly what the strict column
// count is there to catch. So the preview has to show them.
//
// EMPTY for every other movement type. A Forward must not grow two permanently
// blank columns -- a column that is always empty reads as data we failed to
// load, which is the opposite of what this table is for.
//
// The serial is excluded because the table already has it as its first column.
export const previewExtraColumns = (parsed) =>
    (parsed?.columns || []).filter((column) => column.key !== "serialNumber");

// The server's verdicts joined to what the user typed.
//
// THE VALUES ARE NOT IN THE RESPONSE. /api/work-orders returns the serial, the
// verdict, the state and the reason -- it does not echo itemData back. We
// already have the columns from the parse, so they are joined here rather than
// asked for.
//
// Joined on serialKey, the same case-folded identity the rest of this module
// and the backend both match serials by: the report echoes the serial as it
// was sent, but a user who retyped "pk-1" as "PK-1" must not lose their row.
//
// A report row with NO match keeps a null `pasted`. That is not a bug to hide:
// it happens when the box has been edited since the preview ran -- the table
// is dimmed in that state -- and rendering a dash is more honest than
// inventing values or dropping the row.
export const withPastedValues = (rows, parsed) => {
    const list = Array.isArray(rows) ? rows : [];
    if (!previewExtraColumns(parsed).length) return list;

    const byKey = new Map();
    (parsed?.rows || []).forEach((row) => {
        const key = serialKey(row?.serialNumber);
        if (key) byKey.set(key, row.values || {});
    });

    return list.map((row) => ({
        ...row,
        pasted: byKey.get(serialKey(row?.serialNumber)) || null,
    }));
};

// Shown where a column has no answer for this movement type, rather than a
// blank or a dash. Blank reads as unfinished and a dash reads as data we could
// not load; N/A says the question does not apply here.
export const NOT_APPLICABLE = "N/A";

// The Current State cell: the asset's state TODAY, before this movement.
//
// N/A when there isn't one. An asset that does not exist yet has no current
// state, and that is a different fact from one we failed to load -- which is
// what a bare dash reads as.
//
// It used to say "Not registered yet", which only repeated the verdict one
// column to its left. Verdict, Current State and Reason were three columns
// restating each other on every row of an intake.
export const currentStateText = (row) => row?.currentStateCode || NOT_APPLICABLE;

// Why a serial was REFUSED. Nothing else.
//
// The server sends a reason only when there is one; it is null for an accepted
// serial and null for one being registered. The table used to fill those nulls
// with "Ready to move" and "Will be registered by this movement", which is the
// verdict again in a longer sentence.
export const reasonText = (row) => row?.reason || NOT_APPLICABLE;

// The entity type definition behind a "+ New Customer" option, or null.
//
// Returns the WHOLE type row because that is what CreateEditEntityDialog
// takes: its fields, labels and validation are all read off
// entityDefinition.types[].properties, so a dialog opened without it renders a
// title and no form.
//
// NULL HIDES THE OPTION, and that is the point of returning the definition
// rather than a boolean. Three ways to get null, all of them real:
//
//   - the destination is not a customer          -> the option does not belong
//   - pageMeta has not loaded yet                -> not "no customer type"
//   - the org never defined a customer type      -> a configuration gap
//
// Offering "+ New Customer" in any of those opens a dialog that cannot work,
// which is worse than an option the user never sees.
export const customerEntityTypeForDestination = (entityTypeCode, metaInfo) => {
    if (entityTypeCode !== CUSTOMER_ENTITY_TYPE_CODE) return null;
    const types = metaInfo?.network?.entityDefinition?.types;
    if (!Array.isArray(types)) return null;
    return types.find((type) => type?.code === CUSTOMER_ENTITY_TYPE_CODE) || null;
};

// --------------------------------------------------------------------------
// One asset, or many
// --------------------------------------------------------------------------
//
// MOST WORK ORDERS MOVE ONE ASSET. The paste box was built for the two hundred
// serial case and then left as the only way in, so the common path -- one
// battery, one serial -- was a textarea with a format legend above it and a
// row count below it, all of it describing a bulk operation the user was not
// performing.
//
// So: two modes, single first. The paste box does not go anywhere; it stops
// being compulsory.

export const ENTRY_MODE = {
    SINGLE: "single",
    PASTE: "paste",
};

// Which fields single mode renders, for this movement type.
//
// THE SAME SPEC THE PARSER USES, not a second list. That is the whole property
// worth having: a column added to movementPasteSpec grows a field here, a
// column in the box, a column in the preview table and a key in the payload,
// with no further work and no chance of the four disagreeing. Hand-writing
// three layouts -- one per movement type -- would undo it on the first change.
export const singleEntryColumns = (movementType) =>
    movementPasteSpec(movementType).columns;

export const SERIAL_CONTROL = {
    // Search the registry and pick. The asset exists; the maker is naming one.
    SEARCH: "search",
    // Type it. The asset does NOT exist yet and there is nothing to search.
    TEXT: "text",
};

// THE ASYMMETRY IS REAL, so it is stated rather than left to emerge.
//
// A Forward names an asset the registry already holds, so the field is a
// search: the maker picks a serial that exists and a typo becomes impossible
// to submit silently. A Vendor Intake CREATES the asset, so there is nothing
// to search -- a picker there would offer an empty list forever and imply the
// serial must already be known, which is the opposite of what an intake is.
//
// Same flag as the source control and the paste shape, for the same reason:
// this movement brings assets into existence.
export const serialFieldControl = (movementType) =>
    movementType?.allowsNewAssets === true ? SERIAL_CONTROL.TEXT : SERIAL_CONTROL.SEARCH;

// Single mode's fields -> the ONE row they are, in column order.
//
// This is what makes both modes one payload path rather than two. Single mode
// does not build items of its own: it writes the box, and the box is parsed,
// validated, previewed and submitted by exactly the code the paste path uses.
// There is no second builder to keep in step because there is no second
// builder.
//
// TAB delimited, always. Tab wins delimiter detection, and a tab cannot be
// typed into a single-line field -- so a comma in an item name stays part of
// the item name instead of splitting the row.
//
// An ENTIRELY EMPTY set of fields writes an empty box, not a row of
// separators: "nothing entered yet" and "a row whose every column is blank"
// are different, and only the second is an error worth reporting.
export const singleRowToText = (values = {}, columns = []) => {
    const cells = (columns || []).map((column) =>
        String(values?.[column.key] ?? "").trim()
    );
    if (!cells.some(Boolean)) return "";
    return cells.join("\t");
};

// The single-entry fields a NEW movement opens with, given the text it was
// seeded with.
//
// A selection ticked on the Assets tab arrives as serialText and, at one row,
// opens in single mode -- where nothing reads serialText: the box is DERIVED
// from these values (patchSingle), never the other way round. Seeding this
// empty carried the serial across the navigation and then displayed it
// nowhere, so the form asked for an asset the user had already picked.
//
// The BARE-SERIAL reading, deliberately: no movement type is chosen yet, so
// its extra columns are not known, and a carried selection never carries them.
//
// Paste mode gets nothing: there the box IS the input.
export const seedSingleValues = (serialText = "", entryMode = ENTRY_MODE.SINGLE) =>
    entryMode === ENTRY_MODE.PASTE
        ? {}
        : singleValuesFromParsed(parsePastedRows(serialText));

// The reverse, for one parsed row.
export const singleValuesFromParsed = (parsed) => {
    const row = parsed?.rows?.[0];
    if (!row) return {};
    return { ...(row.values || {}) };
};

// Paste -> Single, and whether it is allowed at all.
//
// LOSING FORTY PASTED SERIALS TO A MIS-CLICK IS UNACCEPTABLE, so a box holding
// more than one row does not quietly become a box holding the first. The
// refusal carries the count and the first row, so the caller can state the
// problem and OFFER the choice rather than making it.
//
// A box with unreadable lines refuses too. Those lines are content the user
// typed, and dropping them is the same loss as dropping a good row -- worse,
// because the user has not seen what was wrong with them yet.
export const switchToSingleEntry = (parsed) => {
    const rows = parsed?.rows || [];
    const errors = parsed?.errors || [];
    const entries = rows.length + errors.length;

    // An empty box is not a loss. Switching into an empty form is exactly what
    // someone who has not started yet expects.
    if (entries === 0) return { ok: true, values: {}, count: 0, reason: "" };

    if (rows.length === 1 && errors.length === 0) {
        return {
            ok: true,
            values: singleValuesFromParsed(parsed),
            count: 1,
            reason: "",
        };
    }

    return {
        ok: false,
        values: singleValuesFromParsed(parsed),
        count: entries,
        reason: `The box holds ${entries} ${entries === 1 ? "row" : "rows"}. Single asset keeps one — switching would discard the rest.`,
    };
};

// Single -> Paste. Named for what it is, though it is the row builder: the
// direction costs nothing because the box is ALREADY what single mode writes.
// Nothing is converted at the moment of switching, so nothing can be lost in
// the conversion.
export const switchToPasteEntry = (values, columns) =>
    singleRowToText(values, columns);

// Required-field errors for single mode, keyed by column.
//
// EVERY COLUMN IS REQUIRED, which is the same rule the parser applies to a
// pasted row -- a row with a blank cell is a shifted column, not a row with an
// optional field left out. Said per FIELD here rather than as "line 1: Loan ID
// is required", because in single mode there is no line 1; there is a Loan ID
// box with nothing in it.
export const singleEntryErrors = (values = {}, columns = []) => {
    const errors = {};
    (columns || []).forEach((column) => {
        if (!String(values?.[column.key] ?? "").trim()) {
            errors[column.key] = `${column.label} is required`;
        }
    });
    return errors;
};

// Which mode a form OPENS in.
//
// Single is the default because one asset is the common case. It is not the
// default when the form is handed more than one asset to start with -- a
// selection carried over from the Assets tab, or an existing request being
// edited -- because showing one field over a box holding twelve serials would
// misrepresent what is about to be submitted.
export const initialEntryMode = (serialCount) =>
    Number(serialCount) > 1 ? ENTRY_MODE.PASTE : ENTRY_MODE.SINGLE;

// --------------------------------------------------------------------------
// Re-opening a request that was already raised
// --------------------------------------------------------------------------

// One API item -> the values its row holds, for the columns this movement has.
//
// The API keeps them in two places and this is the seam: loanReferenceID is a
// real column on assetMovementItem, while itemName and sku live inside
// itemData. Anything the movement does not collect is skipped, so a Forward
// reading a record that somehow carries a loan id still opens as one column.
export const itemToRowValues = (item, columns = []) => {
    const itemData = item?.itemData || {};
    const values = {};
    (columns || []).forEach((column) => {
        const raw =
            column.key === "serialNumber"
                ? item?.serialNumber
                : column.key === "loanReferenceID"
                    ? item?.loanReferenceID
                    : itemData[column.key];
        values[column.key] = String(raw ?? "").trim();
    });
    return values;
};

// A saved movement's items -> the box that edits them.
//
// REBUILDS EVERY COLUMN, which is the fix. It used to write serial numbers
// alone, so re-opening a Delivery showed a blank Loan ID and an intake blank
// Item Name and SKU -- fields that are then required, forcing the maker to
// retype what they had already typed, from a record that had it all along.
//
// Tab delimited, matching what single mode writes, so a re-opened request
// parses by exactly the rule it was created under.
export const itemsToPasteText = (items = [], columns = []) =>
    (Array.isArray(items) ? items : [])
        .filter((item) => item?.serialNumber)
        .map((item) => {
            const values = itemToRowValues(item, columns);
            return (columns || []).map((column) => values[column.key]).join("\t");
        })
        .join("\n");

// --------------------------------------------------------------------------
// Naming an entity type the way the org names it
// --------------------------------------------------------------------------

// `subDistributor` is a CODE. It is what the config lists and what the API
// takes, and it is not a word anybody says out loud.
//
// entityDefinition carries the org's own label for each one, and this page now
// loads pageMeta, so the label is available wherever the code is. The code is
// the fallback and nothing more: an org with no definition still gets a
// readable-ish control rather than an empty one.
export const entityTypeLabel = (entityTypeCode, metaInfo) => {
    const code = String(entityTypeCode ?? "");
    if (!code) return "";
    const types = metaInfo?.network?.entityDefinition?.types;
    if (!Array.isArray(types)) return code;
    const match = types.find((type) => type?.code === code);
    return match?.label || code;
};

// The same, for a list -- the FROM caption names every source type a movement
// allows.
export const entityTypeLabels = (entityTypeCodes = [], metaInfo) =>
    (Array.isArray(entityTypeCodes) ? entityTypeCodes : [])
        .map((code) => entityTypeLabel(code, metaInfo))
        .filter(Boolean);

// --------------------------------------------------------------------------
// Why an action did nothing
// --------------------------------------------------------------------------
//
// A BUTTON THAT LOOKS ENABLED AND DOES NOTHING IS THE WORST OF THE THREE
// STATES it could be in.
//
// Preview with an empty Movement Date set an error on the date field, four
// hundred pixels above the button, with no scroll and nothing beside the
// button itself. The click registered, the form refused, and from where the
// user was sitting the button was broken. It is the same failure as the
// mode-switch prompt rendering below a preview table: feedback placed far from
// the control that caused it.
//
// The fix is general rather than per-field: any action blocked by a field
// somewhere else says so AT the action and points at the field.
//
// The button stays ENABLED. A disabled button with no explanation is the other
// half of this same problem -- it is silent too, and it additionally removes
// the click that would have told the user anything.

// Form order, top to bottom. The FIRST match is reported, because that is the
// field the user should fix first and a list of five is not an instruction.
//
// `field` is the value of the data-blocked-field attribute the control
// carries -- a purpose-built focus hook, deliberately NOT the data-testid,
// which belongs to the test suite and should not quietly become behaviour.
const BLOCKING_FIELDS = [
    {
        key: "movements.0.movementTypeCode",
        field: "movementTypeCode",
        message: "Choose a movement type first",
    },
    {
        key: "movements.0.movementDateTime",
        field: "movementDateTime",
        message: "Add a movement date and time first",
    },
    {
        key: "movements.0.destinationEntityID",
        field: "destinationEntityID",
        message: "Choose a destination first",
    },
    {
        key: "movements.0.serials",
        field: "serials",
        message: "Add the assets this work order moves",
    },
    { key: "remarks", field: "remarks", message: "Add remarks first" },
    {
        key: "duplicateSerials",
        field: "serials",
        message: "The same serial appears more than once",
    },
    { key: "movements", field: "serials", message: "Add a movement first" },
    {
        key: "workOrderTypeCode",
        field: null,
        message: "This organisation has no work order type you can raise",
    },
];

// A single-mode field error, which is keyed per column rather than listed
// above: movements.0.single.<columnKey>.
const SINGLE_FIELD_PREFIX = "movements.0.single.";

// Which error keys a movement edit makes obsolete.
//
// A refusal is about the form AS IT WAS. Nothing cleared errors when the form
// changed, so "Choose a movement type" stayed in red under a dropdown reading
// "Forward" until the next Preview -- the form still refusing something the
// user had just done. An error the user has acted on is worse than no error:
// it says the fix did not work.
//
// TARGETED, not "clear everything on any keystroke": an error about the date
// is still true while the user edits the serial box, and wiping it would hide
// a real problem until they pressed the button again.
//
// The serial box and the single-entry fields are ONE input in two shapes, so a
// change to either clears both spellings of the serials error.
export const errorsAfterMovementChange = (errors = {}, previous = {}, next = {}) => {
    const stale = new Set();
    const changed = (key) =>
        JSON.stringify(previous?.[key] ?? null) !== JSON.stringify(next?.[key] ?? null);

    ["movementTypeCode", "movementDateTime", "destinationEntityTypeCode",
     "destinationEntityID", "sourceEntityID"].forEach((key) => {
        if (changed(key)) stale.add(`movements.0.${key}`);
    });

    if (changed("serialText") || changed("singleValues")) {
        stale.add("movements.0.serials");
        stale.add("duplicateSerials");
        Object.keys(errors || {})
            .filter((key) => key.startsWith(SINGLE_FIELD_PREFIX))
            .forEach((key) => stale.add(key));
    }

    // The destination TYPE changing clears the entity beneath it -- see the
    // patch in MovementBuilder -- so an error about the entity is about a
    // value that no longer exists.
    if (changed("destinationEntityTypeCode")) {
        stale.add("movements.0.destinationEntityID");
    }

    if (!stale.size) return errors || {};
    return Object.fromEntries(
        Object.entries(errors || {}).filter(([key]) => !stale.has(key))
    );
};

export const blockedActionNotice = (errors = {}) => {
    const keys = Object.keys(errors || {});
    if (!keys.length) return null;

    const known = BLOCKING_FIELDS.find(({ key }) => errors[key]);
    if (known) {
        return { message: known.message, field: known.field };
    }

    // Single mode's own required fields. Reported in COLUMN order, which is
    // the order they are drawn in, by taking the first key that is present --
    // singleEntryErrors builds them in that order and object key order is
    // insertion order for string keys.
    const singleKey = keys.find((key) => key.startsWith(SINGLE_FIELD_PREFIX));
    if (singleKey) {
        return {
            // The message the field itself shows, reused verbatim so the two
            // places cannot describe the problem differently.
            message: errors[singleKey],
            field: singleKey.slice(SINGLE_FIELD_PREFIX.length),
        };
    }

    // Something blocked it that this list does not name. Better a vague
    // sentence at the button than silence, and the field errors are still on
    // screen.
    return { message: "Some details are missing — check the fields above", field: null };
};

// --------------------------------------------------------------------------
// A movement date that is already right
// --------------------------------------------------------------------------

// NOW, in the ORG's timezone, in the shape the form holds.
//
// A required field that is correct 95% of the time should not start empty.
// Nearly every movement is recorded as it happens or minutes after, so an
// empty date was a question with an obvious answer that the user kept walking
// past -- and then met again at validation, as a refusal.
//
// getCurrentTimestamp reads the timeZone cookie, so this is the org's clock
// and not the browser's: a support user in another region seeds the same
// instant the org would. Never `new Date()` here, for that reason.
//
// Inside the window by construction -- now always sits between two months back
// and one month forward -- so seeding it can never produce a value the
// validator rejects.
export const defaultMovementDateTime = () =>
    getCurrentTimestamp("YYYY-MM-DD[T]HH:mm");

// --------------------------------------------------------------------------
// Who sets the movement date
// --------------------------------------------------------------------------

// Is the movement date the USER's to set?
//
// NO on a create. A work order records a movement being made now, so the only
// correct value is the clock -- and a required field whose one correct answer
// is "now" is not a question, it is a step to walk past and get wrong. The
// form shows it read-only and stamps it again at submit, so a page left open
// for an hour records the hour it was submitted in.
//
// YES on an edit, and only there: the stored value is a real answer somebody
// gave, and correcting it is the whole point of an edit form.
//
// There is deliberately NO back-dating path. One existed briefly -- a "past
// work order" that started with an empty date -- and it is gone: two doors to
// the same form, differing only in whether one field was writable, is a
// distinction makers had to learn before they could raise anything. Recording
// history is a different job from raising a request, and if it comes back it
// should come back as its own thing rather than as a mode of this one.
export const isMovementDateEditable = ({ isEdit = false } = {}) => Boolean(isEdit);

// --------------------------------------------------------------------------
// Which mode the page opens in, and who decided
// --------------------------------------------------------------------------

// THE QUESTION IS ASKED BEFORE THE PAGE, NOT ON IT.
//
// The Single/Paste toggle sat on the form, which meant every maker answered a
// question about bulk entry while looking at a form built for one asset. It is
// one decision taken once, so it moves to the moment the flow starts: the
// floating button asks, and the page renders the answer.
//
// THREE SOURCES, in order of authority:
//
//   1. a selection carried over from the Assets tab. Ticking rows already
//      answered the question -- one row is one asset, more is a paste -- and
//      asking again would be asking someone to repeat themselves.
//   2. the dialog's answer, for the floating button, which has no selection.
//   3. nothing at all -- a direct URL, a refresh, a bookmark. Falls back to
//      single, which is the common case and the smaller surface.
export const resolveEntryMode = (state) => {
    const serials = prefillSerialsFromState(state);
    if (serials.length) return initialEntryMode(serials.length);

    const asked = state && typeof state === "object" ? state.entryMode : null;
    return asked === ENTRY_MODE.PASTE ? ENTRY_MODE.PASTE : ENTRY_MODE.SINGLE;
};

